// https://stackoverflow.com/questions/14220832/zpl-barcode-missing-a-digit-when-printed

import { Injectable } from "@angular/core";
import {
  Addon,
  AddonBin,
  AddonBinStatusEnum,
  AddonStation,
  BarcodePrintStatusEnum,
  InvoiceHeader,
  ItemDetailAddon,
  ItemDetailStatusEnum,
  JoHeader,
  ReturnRequestDetail,
  ShipBin,
} from "app/model/entities/entity-model";
import { ItemDetail } from "app/model/entities/item-detail";
import { Entity } from "breeze-client";
import _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { DateFns } from "./date-fns";
import { DialogWrapperService } from "./dialog-wrapper.service";
import { PrintFns } from "./print-fns";

@Injectable({ providedIn: "root" })
export class BarcodeService {
  constructor(
    private dialogWrapperService: DialogWrapperService,
    private toastr: ToastrService,
  ) {}

  async printTestBarcode() {
    const zpl = `^XA ^CFA,25
      ^FO20,20^FDManufacturer^FS
      ^FO20,50^FDDescription^FS
      ^FO20,80^FDStyle^FS
      ^FO20,110^FDSize^FS
      ^BY3,2,80 ^FO50,180^BC^FD123456789^FS ^XZ`;
    PrintFns.printZpl(zpl, "barcodePrinter");
  }

  async printAddonBinLabels(addonBinPkgs: { addonBin: AddonBin; addons: Addon[] }[], isReprint: boolean): Promise<any> {
    const bars = [];
    const len = (bars.length = addonBinPkgs.length);
    for (let i = 0; i < len; i++) {
      const pkg = addonBinPkgs[i];
      bars[i] = this.printAddonBinLabel(pkg.addonBin, pkg.addons, isReprint);
    }
    const str = bars.join("\r\n");
    return PrintFns.printZpl(str, "barcodePrinter");
  }

  private printAddonBinLabel(addonBin: AddonBin, addons: Addon[], isReprint: boolean): string {
    let zpl = `
    ^XA
    ^CF0,30
    ^FO20,40^FDIn-Process BIN^FS
    ^CF0,60
    ^FO230,20^FD${addonBin.id}^FS
    ^CF0,30`;

    if (isReprint) {
      zpl += `^FO390,40^FD* REPRINT *^FS`;
    }

    zpl += `
    ^BY3,2,60
    ^FO20,80^BC,,N^FD${addonBin.id}^FS
    ^CF0,25`;

    if (addonBin.addonBinStatusId === AddonBinStatusEnum.Closed) {
      zpl += `^FO20,160^FD* BIN IS CLOSED *^FS`;
    } else if (addonBin.pendingAddonStation) {
      zpl += `^FO20,160^FDIn-Process for ${addonBin.pendingAddonStation?.name}^FS`;
    } else if (addonBin.currentAddonStation) {
      if (addonBin.addonBinStatusId == AddonBinStatusEnum.InProcess) {
        zpl += `^FO20,160^FDIn-Process for ${addonBin.currentAddonStation.name}^FS`;
      } else {
        zpl += `^FO20,160^FDWaiting for ${addonBin.currentAddonStation.name}^FS`;
      }
    }

    zpl += `
      ^FO20,200^GB561,2,2^FS
    `;

    let descriptions = addons.map((x) => this.getAddonDescription(x));
    descriptions = _.uniq(descriptions);
    let printRow = 220;

    descriptions.forEach((x) => {
      zpl += `^FO20,${printRow}^FD${x}^FS\n`;
      printRow += 30;
    });

    zpl += `
    ^XZ
    `;

    return zpl;
  }

  private getAddonDescription(addon: Addon) {
    return addon?.addonStation?.name + " (" + addon?.nameAndLocation?.trim() + ")";
  }

  // requires itemDetailAddons.addons , itemDetailAddons.addonBin and stations.
  async printItemDetailBarcodesAndNewAddonBinLabels(itemDetails: ItemDetail[]) {
    /*     let addons = _.flatMap(this.itemDetails, x => x.itemDetailAddons.map(y => y.addon));
    addons = Array.from(new Set(addons)); */

    if (itemDetails.length == 0) return [];
    let itdas = _.flatMap(itemDetails, (x) => x.itemDetailAddons);
    itdas = Array.from(new Set(itdas));

    const addonBins = _.uniq(itdas.map((x) => x.addonBin));
    const newAddonBins = addonBins.filter((x) => x.addonBinStatusId === AddonBinStatusEnum.New);
    const newAddonBinPkgs = newAddonBins.map((aob) => {
      return {
        addonBin: aob,
        addons: itdas.filter((itda) => itda.addonBinId == aob.id).map((itda) => itda.addon),
      };
    });
    if (newAddonBins.length > 0) {
      await this.printAddonBinLabels(newAddonBinPkgs, false);
    }

    await this.printItemDetailBarcodes(itemDetails);

    itemDetails.forEach((itd) => {
      itd.barcodePrintStatusId =
        itd.itemDetailStatusId === ItemDetailStatusEnum.InInventory
          ? BarcodePrintStatusEnum.PrintedForStock
          : BarcodePrintStatusEnum.PrintedForJobOrder;
    });

    // Change from 'New' to 'Open'  - used to detemine if the addonBin label was ever printed.
    newAddonBins.forEach((aob) => (aob.addonBinStatusId = AddonBinStatusEnum.Open));
    const changedItems = [...itemDetails, ...newAddonBins] as Entity[];

    this.toastr.info("Inventory applied to work-in-process", "Database Activity");

    return changedItems;
  }

  /** Print ZPL labels for all the selected rows */
  public async printItemDetailBarcodes(itemDetails: ItemDetail[]): Promise<any> {
    const bars = [];
    const len = (bars.length = itemDetails.length);
    for (let i = 0; i < len; i++) {
      const itd = itemDetails[i];
      const isReprint = itd.barcodePrintStatusId !== BarcodePrintStatusEnum.NotYetPrinted;
      if (itd.joDetailId != null) {
        bars[i] = this.createJobOrderItemDetailBarCode(itd, isReprint);
      } else {
        bars[i] = this.createInventoryItemDetailBarCode(itd, isReprint);
      }
    }

    const str = bars.join("\r\n");
    return await PrintFns.printZpl(str, "barcodePrinter");
  }

  async printAddonBinBarcode(addonBin: AddonBin) {
    // TODO: Jeff
  }

  public async printJobOrderItemDetailBarCode(itd: ItemDetail, isReprint: boolean) {
    const zpl = this.createJobOrderItemDetailBarCode(itd, isReprint);
    return await PrintFns.printZpl(zpl, "barcodePrinter");
  }

  public createJobOrderItemDetailBarCode(itd: ItemDetail, isReprint: boolean) {
    const pod = itd.poDetail;
    const jod = itd.joDetail;
    const joh = jod.joHeader;
    const prod = itd.product;
    const manufacturer = prod.productType.manufacturer;
    const addons = itd.itemDetailAddons;
    const addonBin = itd.itemDetailAddons[0]?.addonBin;
    const pendingAddonStation = addonBin?.pendingAddonStation;

    let zpl = `
      ^XA
      ^FX Manufacturer and product info
      ^CF0,20
      ^FO20,30^FD${manufacturer.name}^FS 
      ^FO20,55^FD${prod.productType.description}^FS
      ^FO20,80^FD${prod.productType.style}^FS
      ^FO20,105^FD${prod.getBarcodeFeatureChoicesExtract()}^FS

      ^FX Routing info (in box)
      ^FO316,23^GB225,90,2^FS
      ^CF0,35
      ^FO330,40^FD${this.getAddonBinId(addonBin, pendingAddonStation)}^FS
      ^CF0,25
      ^FO330,80^FD${this.getAddonPendingAddonStationName(pendingAddonStation)}^FS

      ^FX Barcode
      ^CF0, 10
      ^BY3,2,60^FS
      ^FO20,140^BC,,Y,N^FD${itd.id}^FS

      ^FX Account, user, etc.
      ^CF0,25
      ^FO20,240^FD${joh.id}^FS
      ^FO240,240^FDShip Date: ${DateFns.fmtDate4Short(joh.origExpectedShipDate)}^FS
      ^FO20,270^FD${joh.getFormalEmployeeName()}^FS
      ^FO240,270^FD${joh.account.accountName}^FS
      ^FO20,300^GB561,2,2^FS`;

    // Add account-owned and inventory program labels if applicable
    let modStartRow = 315;
    if (itd.isAccountOwned && itd.account) {
      zpl += `
      ^CF0,20
      ^FO20,310^FDAcct Owned: ${itd.account.accountName}^FS`;
      modStartRow = 335;
      if (joh.inventoryProgramName) {
        zpl += `
      ^FO20,335^FDInventory Name: ${joh.inventoryProgramName}^FS`;
        modStartRow = 360;
      }
    }

    zpl += `
      ^FX Modifications
      ^CF0,20
      ^FO316,23^GB225,89,2^FS`;

    zpl += this.printAddons(modStartRow, addons) + `^XZ`;
    return zpl;
  }

  private getAddonBinId(addonBin: AddonBin, pendingAddonStation: AddonStation): string {
    if (addonBin && addonBin.addonBinStatusId === AddonBinStatusEnum.Closed) {
      return `* BIN: ${addonBin.id} is CLOSED *`;
    }

    if (!addonBin) {
      return `SHPPRP`;
    }

    return `BIN ${addonBin.id.toString()}`;
  }

  private getAddonPendingAddonStationName(pendingAddonStation: AddonStation): string {
    if (!pendingAddonStation) return "";
    return pendingAddonStation.name;
  }

  // private getAddonBinDescriptionAndStation(
  //   addonBin: AddonBin,
  //   pendingAddonStation: AddonStation
  // ): string {
  //   let binName = "";
  //   if (addonBin && addonBin.addonBinStatusId === AddonBinStatusEnum.Closed) {
  //     return `* BIN ${addonBin.id} is CLOSED *`;
  //   }

  //   if (!addonBin) {
  //     return `* Route directly to SHIP PREP *`;
  //   }

  //   return `BIN ${addonBin.id.toString()}-` + pendingAddonStation.name;
  // }

  private printAddons(startRow: number, addons: ItemDetailAddon[]): string {
    let zplStub = "";
    addons.forEach((x) => {
      zplStub += `^FO20,${startRow.toString()}^FD${x.addon.nameAndLocation}`;
      if (x.additionalInfo) {
        zplStub += ` (${x.additionalInfo})`;
      }
      zplStub += `^FS\n`;
      startRow += 27;
    });
    return zplStub;
  }

  /** Make ZPL label for the given ItemDetail */
  private createInventoryItemDetailBarCode(itd: ItemDetail, isReprint: boolean) {
    const mfg = itd.product.productType.manufacturer.name;
    const desc = itd.product.productType.description;
    const style = itd.product.productType.style;
    const featureChoicesExtract = itd.product.getFeatureChoicesExtract();

    let barcodePos: number;

    // font size and product info section
    let zpl = `^XA ^CFA,25
      ^FO20,20^FD${mfg}^FS
      ^FO20,50^FD${desc}^FS
      ^FO20,80^FD${style}^FS
      ^FO20,110^FD${featureChoicesExtract}^FS`;

    if (itd.joDetail && itd.joDetail.joHeader) {
      // item is on a job order, so print job order section
      const jo = itd.joDetail.joHeader;
      const ship = DateFns.fmtDate4Short(jo.origExpectedShipDate);
      // draw a line
      zpl += `^FO20,255^GB570,2,1^FS`;
      let jozpl = `
          ^FO20,260 ^FDJ/O:${jo.id}^FS
          ^FO320,260 ^FDShip:${ship}^FS
          ^CF0,30
          ^FO20,295 ^FD${jo.empFirstName} ${jo.empLastName}^FS
          ^FO320,295 ^FD${jo.account.accountName}^FS`;

      if (itd.joDetail.joDetailAddons) {
        let count = 0,
          modPos = 300;
        itd.joDetail.joDetailAddons.forEach((joda) => {
          jozpl += `^FO30,${(modPos += 25)} ^FD${++count}. ${joda.addon.nameAndLocation}${
            joda.additionalInfo ? ": " + joda.additionalInfo : ""
          } ^FS\n`;
        });
      }
      zpl += jozpl;
      barcodePos = 145;
    } else {
      // item is inventory
      barcodePos = 180;
    }

    // add barcode
    zpl += `^BY3,2,80 ^FO50,${barcodePos}^BC^FD${itd.id}^FS`;

    // Add account-owned label below barcode if applicable
    if (itd.isAccountOwned && itd.account) {
      const accountLabelPos = barcodePos + 155; // barcode height (80) + spacing (75)
      zpl += `
      ^CF0,25
      ^FO20,${accountLabelPos}^FDAcct Owned: ${itd.account.accountName}^FS`;
      const inventoryProgramName = itd.joDetail?.joHeader?.inventoryProgramName ?? itd.poDetail?.joDetail?.joHeader?.inventoryProgramName;
      if (inventoryProgramName) {
        const invNamePos = accountLabelPos + 30;
        zpl += `
      ^FO20,${invNamePos}^FDInventory Name: ${inventoryProgramName}^FS`;
      }
    }

    zpl += ` ^XZ`;
    return zpl;
  }

  /** Print ZPL label with repair comment */
  printRepairBarcode(rrad: ReturnRequestDetail): Promise<any> {
    const zpl = this.makeRepairBarcode(rrad);
    return PrintFns.printZpl(zpl, "barcodePrinter");
  }

  private makeRepairBarcode(rrad: ReturnRequestDetail): string {
    const jod = rrad.joDetail;
    const jo = jod.joHeader;
    const mfg = jod.product.productType.manufacturer.name;
    const desc = jod.product.productType.description;
    const style = jod.product.productType.style;
    const featureChoicesExtract = jod.product.getFeatureChoicesExtract();

    let explanation = rrad.returnExplanation;
    if (!explanation) {
      explanation = "";
    } else if (explanation.length > 240) {
      explanation = explanation.substring(0, 240);
    }

    const zpl = `^XA ^CFA,25
      ^FO20,20^FD${mfg}^FS
      ^FO20,50^FD${desc}^FS
      ^FO20,80^FD${style}^FS
      ^FO20,110^FD${featureChoicesExtract}^FS

      ^CFA,50 ^FO20,160^FDREPAIR^FS
      ^CFA,25 ^FO280,180^FDJ/O:${jo.id}^FS


      ^FO20,270
      ^FB580,6,0,L,0^FD${explanation}^FS ^XZ`;
    return zpl;
  }

  async printShipBinBarcode(shipBin: ShipBin, joh?: JoHeader): Promise<any> {
    const bin = shipBin.id;
    const source = joh || shipBin.firstJoh;
    const acc = source.account.accountName;
    const add = source.shippingName || "";
    const add1 = source.shippingAddress1 || "";
    const add2a = source.shippingAddress2 || "";
    const add2b = source.shippingAddress3 || "";
    var add2 = add2a && add2b ? add2a + " " + add2b : add2a || add2b;
    var add3 = (source.shippingCity || "") + " " + (source.shippingState || "") + " " + (source.shippingZipCode || "");
    var add4 = source.shippingExt || "";
    if (!add2) {
      add2 = add3;
      add3 = add4;
      add4 = "";
    }
    const sf = source.shipFrequency.name;
    const ed = shipBin.expectedShipDate;
    // this.toastr.success('ShipBin Barcode printing not yet implemented');
    const zpl = `
      ^XA
      ^FX Top section
      ^CF0,40
      ^FO20,40,0^FDShip Bin: ^FS
      ^CF0,60^FO180,27,0^FD${bin}^FS
      ^FX Second section with bar code.
      ^BY3,2,60
      ^FO20,90^BC,,N^FD${bin}^FS
      ^FX Third section frequency and ship date
      ^CF0,55^FO370,80^FD ${DateFns.fmtDate(ed)}^FS
      ^CF0,30^FO390,135^FD${sf}^FS
      ^FX Fourth section account and address
      ^CF0,40^FO20,180^FD${acc}^FS
      ^CF0,30^FO20,235^FD${add1}^FS
      ^FO20,270^FD${add2}^FS
      ^FO20,305^FD${add3}^FS
      ^FO20,340^FD${add4}^FS
      ^XZ
    `;

    return PrintFns.printZpl(zpl, "barcodePrinter");
  }

  /** Print a ship-prep label for an item scanned at the scan station.
   *  Shows item barcode, JO#, expected ship date, ship bin#, product info, employee name. */
  async printShipPrepLabel(itd: ItemDetail, shipBin: ShipBin): Promise<any> {
    const jod = itd.joDetail;
    const joh = jod.joHeader;
    const prod = itd.product;
    const manufacturer = prod.productType.manufacturer;

    const zpl = `
      ^XA
      ^FX Manufacturer and product info
      ^CF0,20
      ^FO20,30^FD${manufacturer.name}^FS
      ^FO20,55^FD${prod.productType.description}^FS
      ^FO20,80^FD${prod.productType.style}^FS
      ^FO20,105^FD${prod.getBarcodeFeatureChoicesExtract()}^FS

      ^FX Ship bin info (in box)
      ^FO316,23^GB225,90,2^FS
      ^CF0,35
      ^FO330,40^FDBin ${shipBin.id}^FS
      ^CF0,25
      ^FO330,80^FD${DateFns.fmtDate4Short(shipBin.expectedShipDate)}^FS

      ^FX Barcode
      ^CF0,10
      ^BY3,2,60^FS
      ^FO20,140^BC,,Y,N^FD${itd.id}^FS

      ^FX Account, user, JO info
      ^CF0,25
      ^FO20,240^FD${joh.id}^FS
      ^FO240,240^FDShip Date: ${DateFns.fmtDate4Short(joh.origExpectedShipDate)}^FS
      ^FO20,270^FD${joh.getFormalEmployeeName()}^FS
      ^FO240,270^FD${joh.account.accountName}^FS
      ^XZ
    `;

    return PrintFns.printZpl(zpl, "barcodePrinter");
  }

  // assumes invh.invoiceDetails have been populated
  async printInvoicePackageBarcode(invh: InvoiceHeader): Promise<any> {
    const jo = invh.joHeader;

    const user = invh.joHeader.account.accountName;
    const invDetails = invh.invoiceDetails;
    const shipBin = invh.shipBin;
    let unitCt = 0;
    invh.invoiceDetails.forEach((p) => {
      unitCt += p.shipQty + p.redistributeQty;
    });
    const ed = shipBin.expectedShipDate;

    const add = jo.shippingName || "";
    const add1 = jo.shippingAddress1 || "";
    const add2a = jo.shippingAddress2 || "";
    const add2b = jo.shippingAddress3 || "";
    var add2 = add2a && add2b ? add2a + " " + add2b : add2a || add2b;
    var add3 = (jo.shippingCity || "") + " " + (jo.shippingState || "") + " " + (jo.shippingZipCode || "");
    var add4 = jo.shippingExt || "";
    if (!add2) {
      add2 = add3;
      add3 = add4;
      add4 = "";
    }

    const zpl = `
      ^XA
      ^FT46,67^A0N,42,43^FH^CI28^FDShip Order: ${jo.id}^FS^CI27
      ^FT46,190^A0N,33,33^FH^CI28^FDAccount User: ${jo.empSalutation} ${jo.empFirstName} ${jo.empLastName}^FS^CI27
      ^FT46,231^A0N,33,33^FH^CI28^FDAccount: ${jo.account.accountName}^FS^CI27
      ^BY4,3,62^FT46,146^BCN,,N,N
      ^FH^FD>:${jo.id}^FS
      ^FT46,289^A0N,42,43^FH^CI28^FDShip Bin: ${shipBin.id}^FS^CI27
      ^FT46,342^A0N,42,43^FH^CI28^FDExpected Ship Date: ${DateFns.fmtDate(ed)}^FS^CI27
      ^FT320,289^A0N,42,43^FH^CI28^FDUnit count: ${unitCt}^FS^CI27
      ^FT46,392^A0N,33,33^FH^CI28^FDShip To: ^FS^CI27
      ^FT190,392^A0N,33,33^FH^CI28^FD${add}^FS^CI27
      ^PQ1,0,1,Y
      ^XZ
    `;
    return PrintFns.printZpl(zpl, "barcodePrinter");
  }

  // removed lines
  // ^FT190,433^A0N,33,33^FH^CI28^FD${add1}^FS^CI27
  // ^FT190,474^A0N,33,33^FH^CI28^FD${add2}^FS^CI27
  // ^FT190,515^A0N,33,33^FH^CI28^FD${add3}^FS^CI27
  // ^FT190,556^A0N,33,33^FH^CI28^FD${add4}^FS^CI27

  // assumes -
  async printAddonBarcode(addon: Addon) {
    this.toastr.success("Addon Barcode printing not yet implemented");
  }
}
